import { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { chatGetStats, chatGetSessions, chatGetSession, chatGetPrompt, chatSavePrompt } from '../../lib/adminApi';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';

function Card({ children, style }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', ...style }}>
      {children}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtPage(p) {
  if (!p) return '—';
  return p === '/' ? 'Home' : p.replace(/^\//, '');
}

export default function Chat() {
  const [activeTab,   setActiveTab]   = useState('dashboard');
  const [loading,     setLoading]     = useState(true);
  const [stats,       setStats]       = useState(null);
  const [sessions,    setSessions]    = useState([]);
  const [viewSession, setViewSession] = useState(null);
  const [viewData,    setViewData]    = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [showLeads,   setShowLeads]   = useState(false);
  const [prompt,      setPrompt]      = useState('');
  const [promptOrig,  setPromptOrig]  = useState('');
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, sess, p] = await Promise.all([
        chatGetStats().catch(() => null),
        chatGetSessions().catch(() => []),
        chatGetPrompt().catch(() => ({ prompt: '' })),
      ]);
      setStats(s);
      setSessions(Array.isArray(sess) ? sess : []);
      setPrompt(p?.prompt || '');
      setPromptOrig(p?.prompt || '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function openSession(sess) {
    setViewSession(sess);
    setViewData(null);
    setViewLoading(true);
    try {
      const d = await chatGetSession(sess.session_id);
      setViewData(d);
    } catch (e) {
      setViewData({ error: e.message });
    } finally {
      setViewLoading(false);
    }
  }

  async function savePrompt() {
    setSaving(true);
    try {
      await chatSavePrompt(prompt);
      setPromptOrig(prompt);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  const totals    = stats?.totals || {};
  const answered  = (totals.total_messages || 0) - (totals.total_errors || 0);
  const errorRate = totals.total_messages > 0
    ? ((totals.total_errors / totals.total_messages) * 100).toFixed(1)
    : '0';

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb' }}>
        <AdminSidebar active="chat" />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="chat" />
      <main style={{ flex: 1, overflowY: 'auto' }}>

        {/* Hero */}
        <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0f3460 50%, #162d4a 100%)`, padding: '28px 36px 24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'repeating-linear-gradient(0deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px)' }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(243,116,40,0.15)', border: '1px solid rgba(243,116,40,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>💬</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>Tilly</h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: 13, marginTop: 3 }}>
                Marketing site AI assistant — visitor conversations &amp; prompt editor
              </p>
            </div>
          </div>
        </div>

        <div style={{ padding: '24px 36px 80px' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 24, background: '#e2e8f0', borderRadius: 10, padding: 4, width: 'fit-content' }}>
            {[['dashboard','Dashboard'],['prompt','System Prompt']].map(([k, l]) => (
              <button key={k} onClick={() => setActiveTab(k)} style={{
                padding: '7px 18px', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                fontWeight: activeTab === k ? 700 : 500,
                background: activeTab === k ? '#fff' : 'transparent',
                color: activeTab === k ? NAVY : '#64748b',
                boxShadow: activeTab === k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}>{l}</button>
            ))}
          </div>

          {/* ── DASHBOARD TAB ── */}
          {activeTab === 'dashboard' && (
            <div>
              {/* Stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
                {[
                  { label: 'Total Sessions',    value: totals.total_sessions  ?? '—', sub: `${totals.sessions_7d ?? 0} last 7 days` },
                  { label: 'Messages Answered', value: answered,               sub: `${totals.total_messages ?? 0} total messages`,  color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
                  { label: 'Errors',            value: totals.total_errors ?? 0, sub: `${errorRate}% error rate`, color: totals.total_errors > 0 ? '#b91c1c' : '#15803d', bg: totals.total_errors > 0 ? '#fef2f2' : '#f0fdf4', border: totals.total_errors > 0 ? '#fca5a5' : '#bbf7d0' },
                  { label: 'Leads Captured',    value: totals.total_leads  ?? '—', sub: 'emails submitted',        color: ORANGE,    bg: '#fff7ed',  border: '#fed7aa', clickable: true },
                ].map((c, i) => (
                  <div key={i}
                    onClick={c.clickable ? () => setShowLeads(true) : undefined}
                    style={{ background: c.bg || '#fff', border: `1px solid ${c.border || '#e2e8f0'}`, borderRadius: 12, padding: '16px 18px', textAlign: 'center', cursor: c.clickable ? 'pointer' : 'default', transition: 'box-shadow 0.15s' }}
                    onMouseEnter={c.clickable ? e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(243,116,40,0.15)' : undefined}
                    onMouseLeave={c.clickable ? e => e.currentTarget.style.boxShadow = 'none' : undefined}
                  >
                    <div style={{ fontSize: 28, fontWeight: 800, color: c.color || NAVY, lineHeight: 1 }}>{c.value}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{c.label}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{c.sub}{c.clickable && <span style={{ marginLeft: 4, color: ORANGE }}>↗</span>}</div>
                  </div>
                ))}
              </div>

              {/* Top pages */}
              {stats?.top_pages?.length > 0 && (
                <Card style={{ marginBottom: 20, maxWidth: 400 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Top Pages</div>
                  {stats.top_pages.map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f8fafc', fontSize: 13 }}>
                      <span style={{ color: '#374151' }}>{fmtPage(p.page)}</span>
                      <span style={{ fontWeight: 600, color: NAVY }}>{p.cnt} session{p.cnt !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </Card>
              )}

              {/* Sessions table */}
              <Card>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
                  Recent Conversations
                </div>
                {sessions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>No conversations yet</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        {['Started','Page','Messages','Errors','Lead','Last Activity',''].map((h, i) => (
                          <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s, i) => (
                        <tr key={s.session_id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{fmtDate(s.started_at)}</td>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{fmtPage(s.page)}</td>
                          <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: NAVY, textAlign: 'center' }}>{s.message_count}</td>
                          <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'center' }}>
                            {s.error_count > 0
                              ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>{s.error_count}</span>
                              : <span style={{ color: '#10b981' }}>0</span>}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'center' }}>
                            {s.lead_captured
                              ? <span title={s.lead_email || ''} style={{ color: ORANGE, fontWeight: 700 }}>✓ {s.lead_email ? s.lead_email.split('@')[0] : ''}</span>
                              : <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(s.last_message_at)}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <button onClick={() => openSession(s)}
                              style={{ background: NAVY, border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                              View Thread →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          )}

          {/* ── PROMPT TAB ── */}
          {activeTab === 'prompt' && (
            <div style={{ maxWidth: 800 }}>
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>System Prompt</div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
                      This is what the AI knows about MasterFileAI. Edit and save to update the chatbot instantly — no redeployment needed.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {prompt !== promptOrig && (
                      <button onClick={() => setPrompt(promptOrig)}
                        style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, color: '#64748b', cursor: 'pointer' }}>
                        Reset
                      </button>
                    )}
                    <button onClick={savePrompt} disabled={saving || prompt === promptOrig}
                      style={{ background: saving || prompt === promptOrig ? '#e2e8f0' : ORANGE, color: saving || prompt === promptOrig ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: saving || prompt === promptOrig ? 'default' : 'pointer' }}>
                      {saving ? '⟳ Saving…' : saved ? '✓ Saved' : 'Save Prompt'}
                    </button>
                  </div>
                </div>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  style={{ width: '100%', minHeight: 520, border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', fontSize: 13, fontFamily: 'ui-monospace, monospace', lineHeight: 1.6, color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                  {prompt.length.toLocaleString()} characters · ~{Math.round(prompt.length / 4).toLocaleString()} tokens
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* ── Leads modal ── */}
        {showLeads && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}
            onClick={() => setShowLeads(false)}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Leads Captured</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Visitors who submitted their email</div>
                </div>
                <button onClick={() => setShowLeads(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                {sessions.filter(s => s.lead_captured).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>No leads captured yet</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        {['Email','Page','Date',''].map((h, i) => (
                          <th key={i} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.filter(s => s.lead_captured).map((s, i) => (
                        <tr key={s.session_id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 10px', fontSize: 13, color: s.lead_email ? NAVY : '#94a3b8', fontWeight: s.lead_email ? 600 : 400 }}>{s.lead_email || 'email not submitted'}</td>
                          <td style={{ padding: '10px 10px', fontSize: 12, color: '#64748b' }}>{fmtPage(s.page)}</td>
                          <td style={{ padding: '10px 10px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(s.started_at)}</td>
                          <td style={{ padding: '10px 10px' }}>
                            <button onClick={() => { setShowLeads(false); openSession(s); }}
                              style={{ background: NAVY, border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#fff', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              View Thread →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Session detail panel ── */}
        {viewSession && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}
            onClick={() => setViewSession(null)}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }}
              onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Conversation</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                    {fmtDate(viewSession.started_at)} · {fmtPage(viewSession.page)} · {viewSession.message_count} message{viewSession.message_count !== 1 ? 's' : ''}
                    {viewSession.lead_captured && <span style={{ marginLeft: 10, color: ORANGE, fontWeight: 700 }}>✓ Lead: {viewSession.lead_email}</span>}
                  </div>
                </div>
                <button onClick={() => setViewSession(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {viewLoading && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>Loading…</div>}
                {viewData?.error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{viewData.error}</div>}
                {viewData?.messages?.map((msg, i) => (
                  <div key={i}>
                    {/* User message */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                      <div style={{ background: ORANGE, color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '9px 13px', fontSize: 13, maxWidth: '80%', lineHeight: 1.5 }}>
                        {msg.user_message}
                      </div>
                    </div>
                    {/* Bot reply */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, marginTop: 2 }}>🤖</div>
                      <div style={{
                        background: msg.had_error ? '#fef2f2' : '#f1f5f9',
                        border: msg.had_error ? '1px solid #fca5a5' : 'none',
                        color: '#1e293b', borderRadius: '4px 14px 14px 14px',
                        padding: '9px 13px', fontSize: 13, maxWidth: '80%', lineHeight: 1.6,
                      }}>
                        {msg.bot_reply}
                        {msg.had_error && <span style={{ fontSize: 10, color: '#b91c1c', marginLeft: 6 }}>⚠ error</span>}
                        {msg.capture_lead && <span style={{ fontSize: 10, color: ORANGE, marginLeft: 6 }}>★ lead triggered</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 10, color: '#cbd5e1', marginTop: 4 }}>{fmtDate(msg.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
